import { useCallback, useContext, useEffect } from 'react';

import { AuthFormContext } from './context';
import type { AuthFormField, AuthValidateRule } from './types';

export const useAuthFormField = ({
    defaultValue,
    name,
    rules,
}: {
    defaultValue?: string;
    name: string;
    rules: AuthValidateRule | Array<AuthValidateRule>;
}): {
    value: string;
    focused: boolean;
    error: string;
    handleInputChangeFocus: (e: ReactFocusEvent<HTMLInputElement>) => void;
    handleInputChangeValue: (e: ChangeEvent<HTMLInputElement>) => void;
} => {
    const store = useContext(AuthFormContext);

    const handleInputChangeFocus = useCallback(
        ({ type }: ReactFocusEvent<HTMLInputElement>) => {
            store.updateField(name, { focused: type === 'focus' });
        },
        [name, store],
    );

    const handleInputChangeValue = useCallback(
        ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            store.updateField(name, { value });
        },
        [name, store],
    );

    useEffect(() => {
        store.add(name, {
            value: defaultValue,
            focused: false,
            rules,
        });

        return () => {
            store.remove(name);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const { error: errorInput, focused, touched, value }: AuthFormField = store.getField(name);
    const error = touched && errorInput;

    return {
        value,
        focused,
        error,
        handleInputChangeFocus,
        handleInputChangeValue,
    };
};
